import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardActions, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import { useLocation, useParams } from "react-router-dom";
import { ApicreateOrUpdateUserByClientId, ApigetUserByClientId, ApiPutUpdateAdminStatus } from "../../../apiService/Core/Apicall.tsx";

interface User {
    userId: number;
    firstName: string;
    lastName: string;
    emailId: string;
    password?: string;
}

const ManageUser: React.FC = () => {
    const location = useLocation();
    // const client = location?.state?.clientId;
    const {clientId} = useParams()
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [open, setOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedUserIndex, setSelectedUserIndex] = useState<number | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
    });

    const [users, setUsers] = useState<User[]>([]);

    const handleOpen = (mode: "add" | "edit", index?: number) => {
        setIsEditMode(mode === "edit");
        setOpen(true);
        setErrors({});

        if (mode === "edit" && index !== undefined) {
            const user = users[index];
            setSelectedUserIndex(index);
            setFormData({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.emailId,
                password: "",
                confirmPassword: "",
            });
        } else {
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                confirmPassword: "",
            });
            setSelectedUserIndex(null);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            confirmPassword: "",
        });
        setErrors({});
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.firstName.trim()) newErrors.firstName = "First Name is required";
        if (!formData.lastName.trim()) newErrors.lastName = "Last Name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        if (!isEditMode || formData.password || formData.confirmPassword) {
            if (!formData.password) newErrors.password = "Password is required";
            else if (formData.password.length < 6)
                newErrors.password = "Password must be at least 6 characters";

            if (!formData.confirmPassword)
                newErrors.confirmPassword = "Confirm Password is required";
            else if (formData.password !== formData.confirmPassword)
                newErrors.confirmPassword = "Passwords do not match";
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email))
            newErrors.email = "Invalid email format";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        const payload: any = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            emailId: formData.email,
            password: formData.password,
            clientId: clientId,
            CreatedBy: "admin",
        };

        if (isEditMode && selectedUserIndex !== null) {
            payload.isedit = true;
            payload.userId = users[selectedUserIndex]?.userId;
            payload.ModifiedBy = "admin";
        } else {
            payload.CreatedBy = "admin";
        }

        try {
            const response = await ApicreateOrUpdateUserByClientId(payload);
            if (response?.status) {
                toast.success(isEditMode ? "User updated successfully" : "User created successfully");
                handleGetUser();
                handleClose();
            } else {
                toast.error(response?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error saving user", error);
            toast.error(error)
        }
    };

    const handleGetUser = async () => {
        try {
            const data = `?clientId=${clientId}`
            const response = await ApigetUserByClientId(data);
            setUsers(response?.data);

        } catch (error: any) {
            console.error("Error Generating User.", error)
            const apiMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Error saving user";
            toast.error(apiMessage)
        }
    }

    useEffect(() => {
        handleGetUser()
    }, [])

    const handleDeleteUser = async (userId: number, status: string) => {
        try {
            const data = {
                userId: userId,
                status: status
            }
            const response = await ApiPutUpdateAdminStatus(data);
            toast.success("User deleted successfully.")
            handleGetUser();
        } catch (error) {
            console.error("Error deleting User.", error);
            toast.error("Error Deleting User.")
        }
    }

    return (
        <Box className="p-4" sx={{ background: "white" }}>
            <Box className="p-2 text-center mb-3" sx={{ position: "relative" }}>
                <Typography variant="h3">Manage Users</Typography>
                <Button
                    variant="contained"
                    className="table-row-background"
                    sx={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}
                    onClick={() => handleOpen("add")}
                >
                    Add Users
                </Button>
            </Box>
            <Grid container spacing={2} className="mt-2">
                {users.map((user, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                        <Card sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {user.firstName} {user.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {user.emailId}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <Stack direction="row" spacing={1}>
                                    <Button variant="outlined" color="#ADB792" size="small" onClick={() => handleOpen("edit", index)}>
                                        Edit
                                    </Button>
                                    <Button variant="contained" className="table-row-background" size="small"
                                        onClick={() => {
                                            const isConfirmed = window.confirm("Are you sure you want to delete this user?")
                                            if (isConfirmed) {
                                                handleDeleteUser(user.userId, "delete")
                                            }
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </Stack>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {isEditMode ? "Edit User" : "Add New User"}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                        />
                        <TextField
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                        />
                        <TextField
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                        <TextField
                            label="Password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.password}
                            helperText={errors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label="Confirm Password"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            fullWidth
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="#ADB792" size="small" onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" className="table-row-background" size="small" onClick={handleSave}>
                        {isEditMode ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageUser;
